import React from 'react';

// ─── PROFESSIONAL LINE ICONS ─────────────────────────────────────────────────
// One consistent 24×24 stroke icon per furniture / lighting / opening / wall-
// decor type, plus the room-category icons used by the furniture picker.
// All use currentColor so the parent can tint them. Replaces the emoji glyphs
// that used to sit in FURNITURE_TYPES / LIGHTING_TYPES / WALL_DECOR_META.

const BASE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const PATHS = {
  // ── Furniture ──
  sofa: (
    <>
      <path d="M5 11V8.5A2 2 0 0 1 7 6.5h10a2 2 0 0 1 2 2v2.5" />
      <rect x="2.5" y="11" width="19" height="6" rx="1.6" />
      <path d="M8 11v-1.5M16 11v-1.5M6 17v2M18 17v2" />
    </>
  ),
  armchair: (
    <>
      <path d="M8 11V8.5A2 2 0 0 1 10 6.5h4a2 2 0 0 1 2 2v2.5" />
      <rect x="5.5" y="11" width="13" height="6" rx="1.6" />
      <path d="M8 17v2M16 17v2" />
    </>
  ),
  table: (
    <>
      <rect x="3" y="7.5" width="18" height="3" rx="1" />
      <path d="M6 10.5V19M18 10.5V19" />
    </>
  ),
  chair: (
    <>
      <path d="M8 3.5v9M8 12.5h9" />
      <path d="M8 12.5 6.8 20M17 12.5V20" />
    </>
  ),
  bed: (
    <>
      <path d="M2.5 18v-4a2 2 0 0 1 2-2h13a3 3 0 0 1 3 3v3" />
      <path d="M2.5 15.5h19" />
      <path d="M6.5 12V9.8A1.5 1.5 0 0 1 8 8.3h3A1.5 1.5 0 0 1 12.5 9.8V12" />
      <path d="M2.5 18v1.6M21.5 18v1.6" />
    </>
  ),
  desk: (
    <>
      <rect x="3" y="7.5" width="18" height="2.6" rx="0.6" />
      <path d="M5 10.1V19M19 10.1V19" />
      <rect x="12.5" y="11" width="6" height="5.2" rx="0.6" />
      <path d="M14 13.4h3" />
    </>
  ),
  tv: (
    <>
      <rect x="3" y="4.5" width="18" height="11" rx="1.6" />
      <path d="M9 20h6M12 15.5V20" />
    </>
  ),
  shelf: (
    <>
      <rect x="4.5" y="3" width="15" height="18" rx="1.4" />
      <path d="M4.5 9h15M4.5 15h15" />
      <path d="M8 5.5v2M11 5.5v2M8 11.5v2M14 11.5v2" />
    </>
  ),
  wardrobe: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="1.4" />
      <path d="M12 3v18" />
      <path d="M10 11v2M14 11v2" />
    </>
  ),
  bathtub: (
    <>
      <path d="M3 11.5h18v2.5a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4z" />
      <path d="M6 11.5V8a2 2 0 0 1 2-2" />
      <circle cx="8.4" cy="8.4" r="0.6" />
      <path d="M7 18v1.6M17 18v1.6" />
    </>
  ),
  toilet: (
    <>
      <path d="M7 3.5h6v3.5H7z" />
      <path d="M5 7h10v1.5a5 5 0 0 1-5 5 5 5 0 0 1-5-5z" />
      <path d="M8.5 13.5 7.5 20.5h5l-1-7" />
    </>
  ),
  sink: (
    <>
      <path d="M4 10.5h16v1.6a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5z" />
      <path d="M12 10.5V7a2 2 0 0 1 2-2h1" />
      <circle cx="12" cy="13.6" r="0.7" />
    </>
  ),
  lamp: (
    <>
      <path d="M8.5 4h7l-1.6 6H10.1z" />
      <path d="M12 10v9" />
      <path d="M8.5 19.5h7" />
    </>
  ),
  plant: (
    <>
      <path d="M12 12.5c0-4 3-6.5 3.5-6.8-.2 3.8-1.6 5.8-3.5 6.8z" />
      <path d="M12 12.5c0-4-3-6.5-3.5-6.8.2 3.8 1.6 5.8 3.5 6.8z" />
      <path d="M12 12.5V8.5" />
      <path d="M9 13.5h6l-1 6.5H10z" />
    </>
  ),
  rug: (
    <>
      <rect x="3" y="6" width="18" height="12" rx="1.2" />
      <path d="M7 6v12M17 6v12M3 10h18M3 14h18" />
    </>
  ),

  // ── Lighting ──
  chandelier: (
    <>
      <path d="M12 3v3" />
      <path d="M4 8h16" />
      <path d="M4 8v1a2 2 0 0 0 4 0M10 8v1a2 2 0 0 0 4 0M16 8v1a2 2 0 0 0 4 0" />
      <circle cx="12" cy="6.6" r="1" />
    </>
  ),
  ceiling_lamp: (
    <>
      <path d="M12 3v3" />
      <path d="M7 6.5h10l2 6H5z" />
      <path d="M8 12.5a4 3 0 0 0 8 0" />
    </>
  ),

  // ── Openings ──
  door: (
    <>
      <rect x="6" y="3" width="12" height="18" rx="1" />
      <circle cx="14.5" cy="12" r="1" />
    </>
  ),
  window: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="1.2" />
      <path d="M12 4v16M4 12h16" />
    </>
  ),
  archway: (
    <>
      <path d="M5 21V11a7 7 0 0 1 14 0v10" />
      <path d="M4 21h16" />
    </>
  ),

  // ── Wall decor ──
  painting: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="1.2" />
      <path d="M4 15.5l4-4 3 3 3.5-4.5L20 16" />
      <circle cx="9" cy="9" r="1.2" />
    </>
  ),
  ac: (
    <>
      <rect x="3" y="6" width="18" height="7" rx="1.6" />
      <path d="M6 10h9" />
      <path d="M6 16.5c1-1 2-1 3 0M11 16.5c1-1 2-1 3 0M16 16.5c1-1 2-1 3 0" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v4l3 2" />
    </>
  ),
  outlet: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <circle cx="9.5" cy="10" r="1" />
      <circle cx="14.5" cy="10" r="1" />
      <path d="M12 13.5v2.5" />
    </>
  ),
  radiator: (
    <>
      <rect x="4" y="6" width="16" height="12" rx="1.4" />
      <path d="M8 6v12M12 6v12M16 6v12" />
      <path d="M6.5 18v2M17.5 18v2" />
    </>
  ),

  // ── Time of day (3D view lighting toggle) ──
  morning: (
    <>
      <path d="M5 18a7 7 0 0 1 14 0" />
      <path d="M2 18h20" />
      <path d="M12 6V4M5.6 9.6 4.2 8.2M18.4 9.6l1.4-1.4" />
      <path d="M9 22l3-3 3 3" />
    </>
  ),
  evening: (
    <>
      <path d="M5 18a7 7 0 0 1 14 0" />
      <path d="M2 18h20" />
      <path d="M12 4.5V6.5M5.6 9.6 4.2 8.2M18.4 9.6l1.4-1.4" />
      <path d="M9 19.5l3 3 3-3" />
    </>
  ),
  night: (
    <>
      <path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8Z" />
      <path d="M5 4v2.6M3.7 5.3h2.6" />
      <path d="M8.5 8.2v1.8M7.6 9.1h1.8" />
    </>
  ),

  // ── Room categories ──
  living: (
    <>
      <path d="M5 11V8.5A2 2 0 0 1 7 6.5h10a2 2 0 0 1 2 2v2.5" />
      <rect x="2.5" y="11" width="19" height="6" rx="1.6" />
      <path d="M8 11v-1.5M16 11v-1.5M6 17v2M18 17v2" />
    </>
  ),
  kitchen: (
    <>
      <path d="M3 10h18v2a7 7 0 0 1-7 7h-4a7 7 0 0 1-7-7z" />
      <path d="M3 12H1.5M21 12h1.5" />
      <path d="M8 10V8h8v2" />
      <path d="M12 5v3" />
    </>
  ),
  bedroom: (
    <>
      <path d="M2.5 18v-4a2 2 0 0 1 2-2h13a3 3 0 0 1 3 3v3" />
      <path d="M2.5 15.5h19" />
      <path d="M6.5 12V9.8A1.5 1.5 0 0 1 8 8.3h3A1.5 1.5 0 0 1 12.5 9.8V12" />
      <path d="M2.5 18v1.6M21.5 18v1.6" />
    </>
  ),
  bathroom: (
    <>
      <path d="M12 3c3 4 5 6.5 5 9a5 5 0 0 1-10 0c0-2.5 2-5 5-9z" />
    </>
  ),
  office: (
    <>
      <rect x="3" y="7.5" width="18" height="11.5" rx="2" />
      <path d="M8.5 7.5V5.5a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v2" />
      <path d="M3 12.5h18" />
    </>
  ),
  anywhere: (
    <>
      <path d="M12 3l1.7 4.1L18 9l-4.3 1.9L12 15l-1.7-4.1L6 9l4.3-1.9z" />
      <path d="M18 14.5l.8 1.9L21 17l-2.2.8L18 20l-.8-2.2L15 17l2.2-.6z" />
    </>
  ),
};

export function ItemIcon({ type, size = 22, className }) {
  const glyph = PATHS[type];
  if (!glyph) {
    // Neutral fallback so an unmapped type still renders something clean.
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" {...BASE} className={className}>
        <rect x="4" y="4" width="16" height="16" rx="3" />
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...BASE} className={className}>
      {glyph}
    </svg>
  );
}

export default ItemIcon;
